import { isNull } from 'lodash';
import { useEffect, useRef } from 'react';

import { isInRange } from '~/components/AssetModalV2/Content/Panel/Transcript/utils';
import {
  type RegisterTimestampObserverReturn,
  useVideoTimestampObserverContext,
} from '~/providers/VideoTimestampObserverProvider';
import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { type TimeRange } from '~/swr-hooks/clips/transcript/types';

// This is the number of ms before the end of the current transcript chunk until the next chunk should be fetched
const MIN_BUFFER_TIME_MS = 60 * 1000;

type UseFetchCurrentTranscriptChunkParams = {
  fetchDataForRange: (params: TimeRange) => void;
  fetchedRanges: TimeRange[];
  hasMore: boolean;
  durationMs: number;
};

/**
 * This hook observes the current time in video and fetches the transcript chunk if:
 * - time to the last fetched segment is less than 1 minute
 * - there is no fetched chunk for current video time
 */
export const useFetchCurrentTranscriptChunk = ({
  fetchedRanges,
  fetchDataForRange,
  hasMore,
  durationMs,
}: UseFetchCurrentTranscriptChunkParams) => {
  const currentActiveRange = useRef<TimeRange | null>(null);
  const videoTimeSubscription = useRef<RegisterTimestampObserverReturn>();

  const fetchedRangesRef = useRef(fetchedRanges);
  fetchedRangesRef.current = fetchedRanges;

  const fetchDataForRangeRef = useRef(fetchDataForRange);
  fetchDataForRangeRef.current = fetchDataForRange;

  const { subscribe } = useVideoTimestampObserverContext();

  useEffect(() => {
    if (!hasMore) {
      videoTimeSubscription.current?.unsubscribe();
    }
  }, [hasMore]);

  useEffect(() => {
    videoTimeSubscription.current = subscribe((timeInSeconds) => {
      if (isNull(timeInSeconds)) return;
      const timeInMs = timeInSeconds * 1000;
      if (currentActiveRange.current) {
        const { startTimeMs, endTimeMs } = currentActiveRange.current;
        if (isInRange(timeInMs, [startTimeMs, endTimeMs])) {
          const timeToEnd = endTimeMs - timeInMs;
          if (timeToEnd < MIN_BUFFER_TIME_MS) {
            const endTime = Math.min(durationMs, endTimeMs + MAX_TRANSCRIPT_RANGE_IN_MS);
            currentActiveRange.current = { startTimeMs, endTimeMs: endTime };
            fetchDataForRangeRef.current({ startTimeMs: endTimeMs, endTimeMs: endTime });
          }
          return;
        }
      }

      currentActiveRange.current =
        fetchedRangesRef.current.find((range) => isInRange(timeInMs, [range.startTimeMs, range.endTimeMs])) ?? null;
      if (!currentActiveRange.current) {
        const startTimeMs = Math.max(0, timeInMs - MAX_TRANSCRIPT_RANGE_IN_MS / 2);
        currentActiveRange.current = {
          startTimeMs,
          endTimeMs: startTimeMs === 0 ? MAX_TRANSCRIPT_RANGE_IN_MS : timeInMs + MAX_TRANSCRIPT_RANGE_IN_MS / 2,
        };
        fetchDataForRangeRef.current({
          startTimeMs: currentActiveRange.current.startTimeMs,
          endTimeMs: currentActiveRange.current.endTimeMs,
        });
      }
    });

    return () => {
      videoTimeSubscription.current?.unsubscribe();
    };
  }, [durationMs, subscribe]);
};
